import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { message, notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import { notificationApi } from '../services/api';
import type { StoreNotificationPendingByType, StoreNotificationPollData, StoreWebNotification } from '../types';
import { useAuth } from '../hooks/useAuth';
import { CrossTabLeader } from '../utils/crossTabLeader';
import { reconcilePendingNotifications } from '../utils/reconcilePendingNotifications';

interface StoreNotificationContextValue {
  pendingCount: number;
  pendingByType: StoreNotificationPendingByType;
  unfinishedByType: { wash: number; boarding: number };
  pendingPaymentByType: { wash: number; boarding: number };
  pendingList: StoreWebNotification[];
  pendingListLoading: boolean;
  voiceEnabled: boolean;
  setVoiceEnabled: (enabled: boolean) => Promise<boolean>;
  refreshNotifications: () => Promise<void>;
  loadPendingList: () => Promise<void>;
}

interface NotificationChannelMessage {
  type: 'snapshot' | 'refresh' | 'order_event';
  snapshot?: Partial<StoreNotificationPollData>;
  item?: StoreWebNotification;
}

const VOICE_NOTIFY_KEY = 'store_voice_notify_enabled';
const MAX_SHOWN_KEYS = 200;
const EMPTY_PENDING_BY_TYPE: StoreNotificationPendingByType = {
  wash: 0,
  boarding: 0,
  mall_pickup: 0,
  live_pet_transfer: 0,
};

const defaultState: StoreNotificationContextValue = {
  pendingCount: 0,
  pendingByType: EMPTY_PENDING_BY_TYPE,
  unfinishedByType: { wash: 0, boarding: 0 },
  pendingPaymentByType: { wash: 0, boarding: 0 },
  pendingList: [],
  pendingListLoading: false,
  voiceEnabled: false,
  setVoiceEnabled: async () => false,
  refreshNotifications: async () => undefined,
  loadPendingList: async () => undefined,
};

const StoreNotificationContext = createContext<StoreNotificationContextValue>(defaultState);

function normalizeBadgeData(data?: Partial<StoreNotificationPollData>) {
  return {
    pending_count: Number(data?.pending_count || 0),
    unfinished_by_type: { wash: Number(data?.unfinished_by_type?.wash || 0), boarding: Number(data?.unfinished_by_type?.boarding || 0) },
    pending_payment_by_type: { wash: Number(data?.pending_payment_by_type?.wash || 0), boarding: Number(data?.pending_payment_by_type?.boarding || 0) },
    pending_by_type: {
      wash: Number(data?.pending_by_type?.wash || 0),
      boarding: Number(data?.pending_by_type?.boarding || 0),
      mall_pickup: Number(data?.pending_by_type?.mall_pickup || 0),
      live_pet_transfer: Number(data?.pending_by_type?.live_pet_transfer || 0),
    },
  };
}

function isSamePendingByType(
  left: StoreNotificationPendingByType,
  right: StoreNotificationPendingByType,
) {
  return left.wash === right.wash
    && left.boarding === right.boarding
    && left.mall_pickup === right.mall_pickup
    && left.live_pet_transfer === right.live_pet_transfer;
}

export const StoreNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { user, rbacReady } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [unfinishedByType, setUnfinishedByType] = useState({ wash: 0, boarding: 0 });
  const [pendingPaymentByType, setPendingPaymentByType] = useState({ wash: 0, boarding: 0 });
  const storeIdentityRef = useRef(user?.store_id);
  storeIdentityRef.current = user?.store_id;
  const [pendingByType, setPendingByType] = useState<StoreNotificationPendingByType>(EMPTY_PENDING_BY_TYPE);
  const [pendingList, setPendingList] = useState<StoreWebNotification[]>([]);
  const [pendingListLoading, setPendingListLoading] = useState(false);
  const [voiceEnabled, setVoiceEnabledState] = useState(
    () => localStorage.getItem(VOICE_NOTIFY_KEY) === 'true',
  );
  const [isLeader, setIsLeader] = useState(false);
  const shownKeysRef = useRef<Set<string>>(new Set());
  const shownKeyOrderRef = useRef<string[]>([]);
  const lastSoundAtRef = useRef(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<number | undefined>(undefined);
  const stableTimerRef = useRef<number | undefined>(undefined);
  const refreshDebounceRef = useRef<number | undefined>(undefined);
  const coordinatorRef = useRef<CrossTabLeader | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const performRefreshRef = useRef<() => Promise<void>>(async () => undefined);

  const playNotificationSound = useCallback(async () => {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) throw new Error('当前浏览器不支持音频提示');

    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      audioContextRef.current = new AudioContextClass();
    }
    const audioContext = audioContextRef.current;
    if (audioContext.state === 'suspended') await audioContext.resume();

    const now = audioContext.currentTime;
    const gain = audioContext.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.28, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
    gain.connect(audioContext.destination);
    [880, 1175].forEach((frequency, index) => {
      const start = now + index * 0.18;
      const oscillator = audioContext.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, start);
      oscillator.connect(gain);
      oscillator.start(start);
      oscillator.stop(start + 0.16);
    });
  }, []);

  const playSoundIfEnabled = useCallback(() => {
    if (localStorage.getItem(VOICE_NOTIFY_KEY) !== 'true') return;
    const now = Date.now();
    if (now - lastSoundAtRef.current < 1000) return;
    lastSoundAtRef.current = now;
    playNotificationSound().catch(() => {
      localStorage.setItem(VOICE_NOTIFY_KEY, 'false');
      setVoiceEnabledState(false);
      message.warning('浏览器阻止了语音提示，请点击页面后重新开启');
    });
  }, [playNotificationSound]);

  const setVoiceEnabled = useCallback(async (enabled: boolean) => {
    if (!enabled) {
      localStorage.setItem(VOICE_NOTIFY_KEY, 'false');
      setVoiceEnabledState(false);
      return true;
    }
    try {
      await playNotificationSound();
      localStorage.setItem(VOICE_NOTIFY_KEY, 'true');
      setVoiceEnabledState(true);
      message.success('语音提示已开启，仅当前浏览器生效');
      return true;
    } catch {
      localStorage.setItem(VOICE_NOTIFY_KEY, 'false');
      setVoiceEnabledState(false);
      message.warning('浏览器阻止了语音提示，请点击页面后重新开启');
      return false;
    }
  }, [playNotificationSound]);

  const showOrderNotice = useCallback((item: StoreWebNotification) => {
    const key = item.order_type === 'live_pet_transfer'
      ? `${item.id}-${item.biz_status}`
      : `${item.id}-${item.remind_count}-${item.biz_status}`;
    if (shownKeysRef.current.has(key)) return;
    shownKeysRef.current.add(key);
    shownKeyOrderRef.current.push(key);
    while (shownKeyOrderRef.current.length > MAX_SHOWN_KEYS) {
      const expiredKey = shownKeyOrderRef.current.shift();
      if (expiredKey) shownKeysRef.current.delete(expiredKey);
    }

    const targetUrl = item.payload?.target_url
      || (item.order_type === 'live_pet_transfer'
        ? '/service/live-animal?tab=incoming'
        : (item.order_type === 'boarding'
          ? '/business/boarding-orders?status=0'
          : '/business/wash-orders?status=0'));
    notification.warning({
      key,
      message: item.title || '新的待处理订单',
      description: item.content || item.order_code,
      duration: 8,
      btn: (
        <button
          type="button"
          onClick={() => {
            notification.destroy(key);
            navigate(targetUrl);
          }}
          style={{
            border: 'none', background: '#1677ff', color: '#fff', borderRadius: 4,
            padding: '4px 10px', cursor: 'pointer',
          }}
        >
          去处理
        </button>
      ),
    });
    if ('Notification' in window && window.Notification.permission === 'granted') {
      const browserNotice = new window.Notification(item.title || '新的待处理订单', {
        body: item.content || item.order_code,
      });
      browserNotice.onclick = () => {
        window.focus();
        navigate(targetUrl);
        browserNotice.close();
      };
    }
    playSoundIfEnabled();
  }, [navigate, playSoundIfEnabled]);

  const applyBadgeData = useCallback((data?: Partial<StoreNotificationPollData>, broadcast = false) => {
    const normalized = normalizeBadgeData(data);
    setUnfinishedByType(normalized.unfinished_by_type);
    setPendingPaymentByType(normalized.pending_payment_by_type);
    setPendingCount((current) => (
      current === normalized.pending_count ? current : normalized.pending_count
    ));
    setPendingByType((current) => (
      isSamePendingByType(current, normalized.pending_by_type)
        ? current
        : normalized.pending_by_type
    ));
    const detail = {
      pending_count: normalized.pending_count,
      unfinished_by_type: normalized.unfinished_by_type,
      pending_payment_by_type: normalized.pending_payment_by_type,
      pending_by_type: normalized.pending_by_type,
      pending_list: [],
      remind_list: [],
    };
    window.dispatchEvent(new CustomEvent('store-notification-state', { detail }));
    if (broadcast) {
      coordinatorRef.current?.post({ type: 'snapshot', snapshot: detail });
    }
  }, []);

  const performRefresh = useCallback(() => {
    if (!rbacReady || !user?.store_id) return Promise.resolve();
    if (inFlightRef.current) return inFlightRef.current;
    const identity = user.store_id;
    inFlightRef.current = (async () => {
      try {
        const response: any = await notificationApi.poll(false);
        if (identity === storeIdentityRef.current) applyBadgeData(response.data, true);
      } catch {
        // 气泡对账失败时保留最近状态，SSE 和下一轮轮询会继续补偿。
      } finally {
        if (identity === storeIdentityRef.current) inFlightRef.current = null;
      }
    })();
    return inFlightRef.current;
  }, [applyBadgeData, rbacReady, user?.store_id]);

  useEffect(() => {
    performRefreshRef.current = performRefresh;
  }, [performRefresh]);

  const refreshNotifications = useCallback(async () => {
    const coordinator = coordinatorRef.current;
    if (!coordinator || coordinator.isLeader()) {
      await performRefreshRef.current();
      return;
    }
    coordinator.post({ type: 'refresh' });
  }, []);

  const loadPendingList = useCallback(async () => {
    if (!user?.store_id) return;
    const identity = user.store_id;
    setPendingListLoading(true);
    try {
      const response: any = await notificationApi.list({
        biz_status: 'pending', page: 1, page_size: 20,
      });
      if (identity === storeIdentityRef.current) setPendingList(Array.isArray(response.data?.list) ? response.data.list : []);
    } catch {
      if (identity === storeIdentityRef.current) setPendingList([]);
    } finally {
      if (identity === storeIdentityRef.current) setPendingListLoading(false);
    }
  }, [user?.store_id]);

  const dispatchOrderEvent = useCallback((item: StoreWebNotification, broadcast = false) => {
    window.dispatchEvent(new CustomEvent('store-order-notification', { detail: item }));
    if (item.order_type === 'live_pet_transfer') {
      window.dispatchEvent(new CustomEvent('store-live-pet-transfer-updated', { detail: item }));
    }
    if (broadcast) coordinatorRef.current?.post({ type: 'order_event', item });
  }, []);

  const scheduleBadgeRefresh = useCallback(() => {
    if (refreshDebounceRef.current) window.clearTimeout(refreshDebounceRef.current);
    refreshDebounceRef.current = window.setTimeout(() => {
      refreshDebounceRef.current = undefined;
      performRefreshRef.current();
    }, 500);
  }, []);

  useEffect(() => {
    inFlightRef.current = null;
    applyBadgeData();
    setPendingList([]);
    setPendingListLoading(false);
    shownKeysRef.current.clear();
    shownKeyOrderRef.current = [];
  }, [user?.store_id, applyBadgeData]);

  useEffect(() => {
    if (!rbacReady || !user?.store_id) return undefined;
    const identity = String(user.store_id);
    let coordinator: CrossTabLeader;
    coordinator = new CrossTabLeader({
      channelName: `petflow-store-notification-${identity}`,
      leaseKey: `petflow:store-notification:leader:${identity}`,
      onLeaderChange: setIsLeader,
      onMessage: (rawMessage) => {
        if (identity !== String(storeIdentityRef.current)) return;
        const channelMessage = rawMessage as NotificationChannelMessage;
        if (channelMessage?.type === 'snapshot' && channelMessage.snapshot) {
          applyBadgeData(channelMessage.snapshot, false);
        } else if (channelMessage?.type === 'refresh' && coordinator.isLeader()) {
          performRefreshRef.current();
        } else if (channelMessage?.type === 'order_event' && channelMessage.item) {
          dispatchOrderEvent(channelMessage.item, false);
        }
      },
    });
    coordinatorRef.current = coordinator;
    coordinator.start();
    if (!coordinator.isLeader()) coordinator.post({ type: 'refresh' });
    return () => {
      coordinator.stop();
      if (coordinatorRef.current === coordinator) coordinatorRef.current = null;
      setIsLeader(false);
    };
  }, [applyBadgeData, dispatchOrderEvent, rbacReady, user?.store_id]);

  useEffect(() => {
    const token = localStorage.getItem('store_token');
    if (!token || !isLeader || !user?.store_id) return undefined;
    const identity = user.store_id;
    let disposed = false;
    let failureCount = 0;
    let reconcileTimer: number | undefined;

    const clearTimers = () => {
      if (reconcileTimer) window.clearTimeout(reconcileTimer);
      reconcileTimer = undefined;
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      if (stableTimerRef.current) window.clearTimeout(stableTimerRef.current);
      reconnectTimerRef.current = undefined;
      stableTimerRef.current = undefined;
    };
    const closeEventSource = () => {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
    const scheduleReconnect = (connect: () => void) => {
      failureCount += 1;
      const circuitOpen = failureCount >= 5;
      const baseDelay = circuitOpen
        ? 300000
        : Math.min(60000, 5000 * (2 ** Math.max(0, failureCount - 1)));
      const jitter = Math.floor(Math.random() * Math.max(1, Math.floor(baseDelay * 0.2)));
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = undefined;
        if (circuitOpen) failureCount = 0;
        connect();
      }, baseDelay + jitter);
    };

    const connect = () => {
      if (disposed) return;
      clearTimers();
      closeEventSource();
      const eventSource = new EventSource(notificationApi.streamUrl());
      eventSourceRef.current = eventSource;

      eventSource.addEventListener('connected', () => {
        const isCurrent = () => !disposed && identity === storeIdentityRef.current
          && eventSourceRef.current === eventSource;
        const reconcile = async () => {
          try {
            let notices = 0;
            await reconcilePendingNotifications<StoreWebNotification>(async (page, pageSize) => {
              const response: any = await notificationApi.list({ biz_status: 'pending', page, page_size: pageSize });
              return response.data;
            }, (item) => {
              // 列表全部对账；弹窗限制前20条，避免大量历史待办同时打扰。
              if (notices++ < 20) showOrderNotice(item);
              dispatchOrderEvent(item, true);
            }, isCurrent);
            if (isCurrent()) scheduleBadgeRefresh();
          } catch {
            if (isCurrent()) reconcileTimer = window.setTimeout(reconcile, 30000);
          }
        };
        void reconcile();
        stableTimerRef.current = window.setTimeout(() => {
          failureCount = 0;
          stableTimerRef.current = undefined;
        }, 30000);
      });
      eventSource.addEventListener('notification_created', (event) => {
        if (disposed || identity !== storeIdentityRef.current) return;
        try {
          const item = JSON.parse((event as MessageEvent).data) as StoreWebNotification;
          showOrderNotice(item);
          dispatchOrderEvent(item, true);
          scheduleBadgeRefresh();
        } catch { /* ignore malformed event */ }
      });
      eventSource.addEventListener('notification_remind', (event) => {
        if (disposed || identity !== storeIdentityRef.current) return;
        try {
          showOrderNotice(JSON.parse((event as MessageEvent).data) as StoreWebNotification);
        } catch { /* ignore malformed event */ }
      });
      eventSource.addEventListener('notification_updated', (event) => {
        if (disposed || identity !== storeIdentityRef.current) return;
        try {
          const item = JSON.parse((event as MessageEvent).data) as StoreWebNotification;
          dispatchOrderEvent(item, true);
        } catch { /* the badge refresh below still reconciles state */ }
        scheduleBadgeRefresh();
      });
      eventSource.addEventListener('live_pet_transfer_updated', (event) => {
        if (disposed || identity !== storeIdentityRef.current) return;
        try {
          const detail = JSON.parse((event as MessageEvent).data) as StoreWebNotification;
          dispatchOrderEvent(detail, true);
        } catch {
          window.dispatchEvent(new CustomEvent('store-live-pet-transfer-updated'));
        }
        scheduleBadgeRefresh();
      });
      eventSource.onerror = () => {
        if (eventSourceRef.current === eventSource) closeEventSource();
        else eventSource.close();
        if (!disposed && !reconnectTimerRef.current) scheduleReconnect(connect);
      };
    };

    connect();
    return () => {
      disposed = true;
      clearTimers();
      closeEventSource();
    };
  }, [dispatchOrderEvent, isLeader, scheduleBadgeRefresh, showOrderNotice, user?.store_id]);

  useEffect(() => {
    if (!rbacReady || !isLeader) return undefined;
    let disposed = false;
    let timer: number | undefined;
    const schedule = () => {
      if (disposed || document.hidden) return;
      timer = window.setTimeout(tick, 30000);
    };
    const tick = async () => {
      await performRefreshRef.current();
      schedule();
    };
    const onVisible = () => {
      if (timer) window.clearTimeout(timer);
      timer = undefined;
      if (!document.hidden) performRefreshRef.current().finally(schedule);
    };
    performRefreshRef.current().finally(schedule);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      if (timer) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isLeader, rbacReady]);

  useEffect(() => {
    const handleMallPickupUpdated = () => refreshNotifications();
    window.addEventListener('store-mall-pickup-updated', handleMallPickupUpdated);
    return () => window.removeEventListener('store-mall-pickup-updated', handleMallPickupUpdated);
  }, [refreshNotifications]);

  useEffect(() => () => {
    if (refreshDebounceRef.current) window.clearTimeout(refreshDebounceRef.current);
    audioContextRef.current?.close().catch(() => undefined);
    audioContextRef.current = null;
    shownKeysRef.current.clear();
    shownKeyOrderRef.current = [];
  }, []);

  const value = useMemo(() => ({
    pendingCount,
    pendingByType,
    unfinishedByType,
    pendingPaymentByType,
    pendingList,
    pendingListLoading,
    voiceEnabled,
    setVoiceEnabled,
    refreshNotifications,
    loadPendingList,
  }), [
    loadPendingList, pendingByType, pendingCount, pendingList, pendingListLoading,
    unfinishedByType, pendingPaymentByType,
    refreshNotifications, setVoiceEnabled, voiceEnabled,
  ]);

  return (
    <StoreNotificationContext.Provider value={value}>
      {children}
    </StoreNotificationContext.Provider>
  );
};

export function useStoreNotifications() {
  return useContext(StoreNotificationContext);
}
